import React, { useCallback, useEffect, useState } from 'react';
import { FieldArray } from 'formik';
import {
  Box,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { useSnackbar } from 'notistack';
import ProdcutPriceModalContainer from '../../../containers/chancemng/costcalc/ProdcutPriceModalContainer';
import ChanceCostCalcUploadDialog from '../../../containers/chancemng/ChanceCostCalcUploadDialog';
import AskDialog from '../../common/dialog/AskDialog';
import CostCalcTable from './CostCalcTable';
import CostCalcEditPanel from './CostCalcEditPanel';
import { useBoolean } from '../../../hooks/useBoolean';
import * as chanceApi from '../../../lib/api/chance';
import { downloadFile } from '../../../utils/fileCodeUtils';
import { computeRowAmounts, sumCostCalcs, validateCostRow } from './costCalcShared';

// 원가 산출 내역 - 옵션 B(owner 승인, bito feat/ui-modern 29d6fbb 포팅): 보기전용 표 + 행을
// 클릭하면 열리는 우측 패널. 전에는 표 칸 안에 입력칸이 전부 들어있는 인라인 편집 표였다 -
// 표가 복잡해지고 1440px에서도 가로 스크롤이 남는 문제가 반복됐다. 지금은 CostCalcTable(값만
// 보여줌, 편집/확정 공용)과 CostCalcEditPanel(선택된 한 줄만 고치는 패널)로 역할을 나눴다.
//
// KMES 전용 유지 사항: 엑셀 템플릿은 서버가 매번 생성해주고(거래처 드롭다운 포함) prjUuid가
// 있으면 "현재 내역"도 같은 칸 배치로 내려받아 왕복 편집할 수 있다(2026-10-27
// cost-excel-roundtrip). 업로드 결과는 ChanceCostCalcUploadDialog가 먼저 미리보기(신규/제안/
// 오류)로 보여주고, "확인" 후 넘어온 정상 행만 refreshOnClose로 받아 여기서 push한다 -
// 엑셀에 실제로 입력된 매입처(sellPartnerUuid/Nm)를 그대로 쓴다(1c24af4/이 포팅에서도 유지).
const CostCalcListForm = ({
  formik,
  ArrayHelperRef,
  ApiRef,
  itemCdOptions,
  selectedVendor,
  onSelectedProducts,
  onAddEtcProduct,
  prjUuid,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();

  const [openProduct, setOpenProduct] = useState(false);
  const handleOpenProduct = () => setOpenProduct(true);
  const handleCloseProduct = () => setOpenProduct(false);

  const [selectedIndex, setSelectedIndex] = useState(null);
  const [deleteIndex, setDeleteIndex] = useState(null);

  const showUpload = useBoolean();
  const handleUpload = useCallback(() => {
    showUpload.onTrue();
  }, [showUpload]);

  // 2026-10-27 cost-excel-roundtrip: 고정된 S3 템플릿 링크 대신 서버가 매번 최신(매입처
  // 드롭다운 포함)으로 생성해주는 템플릿을 받는다 - 거래처가 바뀌어도 템플릿이 낡지 않는다.
  const handleDownloadTemplate = async () => {
    try {
      const blob = await chanceApi.downloadCostTemplate();
      const filename = blob.headers['filename'];
      downloadFile(blob, decodeURIComponent(filename), 'xlsx');
    } catch (err) {
      const message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  // 현재 저장된 원가산출 내역을 템플릿과 같은 칸 배치로 받는다 - 받은 파일을 고쳐서 그대로
  // 다시 업로드하면 왕복이 된다(owner decision 2). 아직 저장 전(prjUuid 없음)이면 비활성화.
  const handleDownloadCurrent = async () => {
    if (!prjUuid) return;
    try {
      const blob = await chanceApi.downloadCostCurrent(prjUuid);
      const filename = blob.headers['filename'];
      downloadFile(blob, decodeURIComponent(filename), 'xlsx');
    } catch (err) {
      const message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const [menuAnchor, setMenuAnchor] = useState(null);
  const openMenu = (e) => setMenuAnchor(e.currentTarget);
  const closeMenu = () => setMenuAnchor(null);

  const costCalcs = formik.values?.costCalcs || [];
  const count = costCalcs.length;
  const totals = sumCostCalcs(costCalcs);

  // 패널이 떠 있는 줄이 삭제 등으로 사라지면(count만 줄어드는 경우 포함) 범위 밖을 가리키지
  // 않게 보정한다.
  useEffect(() => {
    if (selectedIndex !== null && selectedIndex >= count) {
      setSelectedIndex(count > 0 ? count - 1 : null);
    }
  }, [count, selectedIndex]);

  const rows = costCalcs.map((cost, index) => {
    const { saleVal, buyVal, profit, profitRate } = computeRowAmounts(cost);
    const errors = validateCostRow(cost);
    return {
      key: index,
      no: index + 1,
      isEtc: cost?.masterTyp !== 'N',
      productNm: cost.productNm,
      productDesc: cost.productDesc,
      qty: cost.expCnt,
      unit: cost.productUnit,
      sellPrice: cost.unitVal,
      sellAmount: saleVal,
      buyPrice: cost.priceVal,
      buyAmount: buyVal,
      partnerNm: cost.sellPartnerNm,
      profit,
      profitRate,
      missingPartner: !cost.sellPartnerNm,
      invalid: Object.keys(errors).length > 0,
    };
  });

  // 패널 "적용"/"다음"/"이전" - 패널 안 draft 값을 formik(폼 상태)에 실제로 반영한다. 패널은
  // 타이핑 중엔 formik을 건드리지 않고 "적용" 버튼을 눌러야 표/합계가 그 값으로 갱신된다.
  const writeBackRow = (index, values) => {
    const base = `costCalcs[${index}]`;
    Object.entries(values).forEach(([field, value]) => {
      formik.setFieldValue(`${base}.${field}`, value, false);
    });
    const errors = validateCostRow(values);
    Object.keys(errors).forEach((field) => {
      formik.setFieldTouched(`${base}.${field}`, true, false);
    });
  };

  const handlePanelApply = (values) => {
    if (selectedIndex === null) return;
    writeBackRow(selectedIndex, values);
  };
  const handlePanelNavigate = (values, direction) => {
    if (selectedIndex === null) return;
    writeBackRow(selectedIndex, values);
    const next = selectedIndex + direction;
    if (next >= 0 && next < count) {
      setSelectedIndex(next);
    }
  };
  const handlePanelNext = (values) => handlePanelNavigate(values, 1);
  const handlePanelPrev = (values) => handlePanelNavigate(values, -1);
  const handlePanelClose = () => setSelectedIndex(null);
  const handleRequestDelete = () => setDeleteIndex(selectedIndex);
  const handleCancelDelete = () => setDeleteIndex(null);
  const handleConfirmDelete = () => {
    if (deleteIndex === null) return;
    ArrayHelperRef.current.remove(deleteIndex);
    setDeleteIndex(null);
    setSelectedIndex(null);
  };

  // 헤더 "제품 추가"/"기타 제품 추가"로 새 줄이 생기면 그 줄(맨 끝에 push됨)에 바로 패널을
  // 연다 - 추가 직후 사용자가 바로 매입처/단가를 채워 넣게 하기 위해서.
  const handleProductsSelected = (data) => {
    const startIndex = costCalcs.length;
    onSelectedProducts(data);
    setSelectedIndex(startIndex);
  };
  const handleAddEtc = () => {
    const startIndex = costCalcs.length;
    onAddEtcProduct();
    setSelectedIndex(startIndex);
  };

  const refreshOnClose = (data) => {
    // eslint-disable-next-line array-callback-return
    data.map((item) => {
      ArrayHelperRef.current.push({
        // [버그수정, Fable 사전검토 3-3] 전부 'E'(기타)로 고정하면 코드/이름으로 MATCHED된
        // 진짜 제품(ExpenseService#applyProductMatch가 masterTyp='N'을 세팅)과 미리보기
        // "추가"로 막 등록한 신규 제품(위 handleConfirmAdd가 masterTyp='N'을 세팅)도 전부
        // "기타" 칩이 붙는 N형과 다르게 동작했다. 서버/다이얼로그가 이미 세팅해 준 값을 쓰고,
        // 둘 다 없을 때만(옛 응답·진짜 기타 라인) 'E'로 기본값을 둔다.
        masterTyp: item?.masterTyp || 'E',
        priceUuid: item?.priceUuid,
        // 2026-10-27 security-patches: 백엔드(ExpenseService#uploadFileList)가 엑셀의
        // 회사명으로 찾은/대체한 매입처를 sellPartnerUuid/sellPartnerNm으로 돌려준다
        // (1c24af4). 예전엔 여기서 그 값을 버리고 빈 문자열/고정 이름으로 덮어써서, 엑셀에
        // 실제 거래처명을 적어도 저장 시 항상 "매입처 없음"으로 막히거나 ETC로 대체됐다.
        sellPartnerUuid: item?.sellPartnerUuid,
        sellPartnerNm: item?.sellPartnerNm,
        partnerUuid: item?.partnerUuid,
        partnerNm: item?.partnerNm,
        productNm: item?.productNm,
        productUuid: item?.productUuid,
        expCnt: item?.expCnt,
        unitVal: item?.unitVal,
        priceVal: item?.priceVal,
        saleVal: item?.saleVal,
        buyVal: item?.buyVal,
        profit: item?.salePct,
        profitRate: item?.saleProfit,
        productDesc: item?.productDesc,
        productUnit: item?.productUnit,
        itemCd: item?.itemCd ?? '기타', // '' (신규 등록, 분류 미지정)는 유지 - '기타'로 되돌리지 않는다
      });
    });

    showUpload.onFalse();
  };

  const selectedCost = selectedIndex !== null ? costCalcs[selectedIndex] : null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
      <Paper variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden', flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
            px: 2,
            py: 1.25,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} component="div">
              원가 산출
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {count}개 품목
            </Typography>
            {formik.dirty && (
              <Tooltip title='"저장" 버튼을 눌러야 "원가산출 확정"과 새로고침 후에도 반영됩니다.' arrow>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    component="span"
                    sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'warning.main', flexShrink: 0 }}
                  />
                  <Typography variant="caption" fontWeight={600} sx={{ color: 'warning.dark' }}>
                    저장 안 됨
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <IconButton aria-label="더보기" size="small" onClick={openMenu}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
              <MenuItem
                onClick={() => {
                  handleDownloadTemplate();
                  closeMenu();
                }}
              >
                <ListItemIcon>
                  <FileDownloadOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>엑셀 양식 받기</ListItemText>
              </MenuItem>
              <MenuItem
                disabled={!prjUuid}
                onClick={() => {
                  handleDownloadCurrent();
                  closeMenu();
                }}
              >
                <ListItemIcon>
                  <FileDownloadOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="현재 내역 다운로드"
                  secondary={!prjUuid ? '먼저 저장한 뒤 이용할 수 있습니다' : undefined}
                />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleUpload();
                  closeMenu();
                }}
              >
                <ListItemIcon>
                  <FileUploadOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>엑셀 업로드</ListItemText>
              </MenuItem>
            </Menu>
            <Button variant="outlined" size="small" color="warning" onClick={handleAddEtc}>
              기타 제품 추가
            </Button>
            <Button
              variant="contained"
              size="small"
              color="secondary"
              startIcon={<AddIcon fontSize="small" />}
              onClick={handleOpenProduct}
            >
              제품 추가
            </Button>
          </Box>
        </Box>

        <FieldArray
          name="costCalcs"
          render={(arrayHelpers) => {
            ArrayHelperRef.current = arrayHelpers;

            if (ApiRef) {
              ApiRef.current = {
                // "원가산출 확정" 전에 호출 - 유효하지 않은 줄이 있으면 그 줄을 선택해 패널을
                // 열어주고 false를 돌려준다(확정 API 호출을 막는 용도).
                validateAndFocusFirstInvalid: () => {
                  const list = formik.values?.costCalcs || [];
                  const idx = list.findIndex((c) => Object.keys(validateCostRow(c)).length > 0);
                  if (idx === -1) return true;
                  const errors = validateCostRow(list[idx]);
                  Object.keys(errors).forEach((field) => {
                    formik.setFieldTouched(`costCalcs[${idx}].${field}`, true, false);
                  });
                  setSelectedIndex(idx);
                  return false;
                },
              };
            }

            if (count === 0) {
              return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    원가산출 내역이 없습니다. 제품을 선택하여 원가산출을 등록하세요.
                  </Typography>
                </Box>
              );
            }

            return (
              <CostCalcTable
                rows={rows}
                totals={totals}
                editable
                selectedIndex={selectedIndex}
                onRowClick={setSelectedIndex}
              />
            );
          }}
        />
      </Paper>

      {selectedCost && (
        <CostCalcEditPanel
          cost={selectedCost}
          index={selectedIndex}
          count={count}
          itemCdOptions={itemCdOptions}
          onApply={handlePanelApply}
          onNext={handlePanelNext}
          onPrev={handlePanelPrev}
          onRequestDelete={handleRequestDelete}
          onClose={handlePanelClose}
          isMobile={isMobile}
        />
      )}

      <AskDialog
        visible={deleteIndex !== null}
        title="품목 삭제"
        description="이 품목을 삭제하시겠습니까?"
        confirmText="삭제"
        onConfirm={handleConfirmDelete}
        onCancle={handleCancelDelete}
      />

      <Box>
        <ProdcutPriceModalContainer
          title="원가 산출 제품 선택"
          open={openProduct}
          handleClose={handleCloseProduct}
          onSelectedProducts={handleProductsSelected}
          selectedPartner={selectedVendor}
        />
        <ChanceCostCalcUploadDialog
          title="원가산출 엑셀 업로드"
          open={showUpload.value}
          onClose={() => {
            showUpload.onFalse();
          }}
          refreshOnClose={refreshOnClose}
        />
      </Box>
    </Box>
  );
};

export default CostCalcListForm;
